import React from 'react';
import { useTranslation } from 'react-i18next';
import ColumnManager from './ColumnManager';
import DocumentTableToolbar from './DocumentTableToolbar';
import NestedTableHeader from './NestedTableHeader';
import NestedTableBody from './NestedTableBody';
import BulkDeleteModal from './BulkDeleteModal';
import DocumentNestedTableFooter from './DocumentNestedTableFooter';
import IssueSerialsModal from '../modals/IssueSerialsModal';
import DownloadExcel from '../assets/DownloadExcel';
import UploadExcel from '../assets/UploadExcel';
import { useNestedDocumentRows } from '../hooks/useNestedDocumentRows';
import { useNestedDocumentSelection } from '../hooks/useNestedDocumentSelection';
import { useNestedDocumentQuantityModal } from '../hooks/useNestedDocumentQuantityModal';
import { useNestedDocumentColumns } from '../hooks/useNestedDocumentColumns';
import { useNestedDocumentSearchSort } from '../hooks/useNestedDocumentSearchSort';
import { useNestedDocumentExcel } from '../hooks/useNestedDocumentExcel';
import { useNestedDocumentKeyboard } from '../hooks/useNestedDocumentKeyboard';

interface NestedDocumentTableProps {
  documentId: string;
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  resetTrigger?: number;
  documentTypePartnerStock?: string | null; // Partner stock from document type definition
  batch?: number; // Batch value from work order header for calculating quantityOS
  documentCategory?: "material" | "non-material" | "planning" | "work-order"; // Document category to determine if tabs should be shown
  defaultPartner?: { _id: string; name?: string } | null;
  isLocked?: boolean; // Lock/disable the table after creating 6421 document
}

const NestedDocumentTable: React.FC<NestedDocumentTableProps> = ({ documentId, inputRefs, resetTrigger, documentTypePartnerStock, batch, documentCategory, defaultPartner, isLocked = false }) => {
  const { t } = useTranslation();

  const columns = useNestedDocumentColumns();

  const rows = useNestedDocumentRows({
    documentId,
    inputRefs,
    resetTrigger,
    batch,
    documentCategory,
    defaultPartner,
    columnOrder: columns.columnOrder,
  });

  const selection = useNestedDocumentSelection({
    localRows: rows.localRows,
    deleteDocumentLineItemWorkOrder: rows.deleteDocumentLineItemWorkOrder,
    refetch: rows.refetch,
  });

  const quantityModal = useNestedDocumentQuantityModal({
    documentId,
    localRows: rows.localRows,
    setLocalRows: rows.setLocalRows,
    editDocumentLineItemWorkOrder: rows.editDocumentLineItemWorkOrder,
    createDocumentLineItemWorkOrder: rows.createDocumentLineItemWorkOrder,
    refetch: rows.refetch,
    setSavingRows: rows.setSavingRows,
  });

  const searchSort = useNestedDocumentSearchSort({
    localRows: rows.localRows,
    columnOrder: columns.columnOrder,
    inputRefs,
    focusLastRow: rows.focusLastRow,
  });

  const excel = useNestedDocumentExcel({
    documentId,
    defaultPartner,
    columnOrder: columns.columnOrder,
    inputRefs,
    localRows: rows.localRows,
    setLocalRows: rows.setLocalRows,
  });

  const keyboard = useNestedDocumentKeyboard({
    columnOrder: columns.columnOrder,
    hiddenColumns: columns.hiddenColumns,
    inputRefs,
    localRows: rows.localRows,
    setLocalRows: rows.setLocalRows,
    createEmptyRow: rows.createEmptyRow,
    handleSaveAllPendingRows: rows.handleSaveAllPendingRows,
  });

  // Render table content (used in both tabbed and non-tabbed views)
  const renderTableContent = () => (
    <>

      {/* Document Table Toolbar */}
      <DocumentTableToolbar
        searchInput={searchSort.searchInput}
        setSearchInput={searchSort.setSearchInput}
        isSearchOpen={searchSort.isSearchOpen}
        setIsSearchOpen={searchSort.setIsSearchOpen}
        handleSearch={searchSort.handleSearch}
        clearSearch={searchSort.clearSearch}
        searchPlaceholder={t('search.searchItems') || "Search items..."}
        handleDownloadExcel={excel.handleDownloadExcel}
        showPlusButton={true}
        showExcelButtons={true}
        // No tabs = no tab navigation on the right
      />

      {/* Table Container */}
      <div className="overflow-auto w-full max-h-[600px] border border-gray-300 border-t-0 bg-white shadow-sm pb-48 relative">
        <table className="w-full border-collapse text-sm table-fixed" style={{ width: '100%', minWidth: '0' }}>
        <NestedTableHeader
          localRows={rows.localRows}
          selectedRows={selection.selectedRows}
          handleSelectAll={selection.handleSelectAll}
          handleDeleteSelected={selection.handleDeleteSelected}
          columnOrder={columns.columnOrder}
          hiddenColumns={columns.hiddenColumns}
          columnConfig={columns.columnConfig}
          columnWidths={columns.columnWidths}
          resizing={columns.resizing}
          draggedColumn={columns.draggedColumn}
          dragOverColumn={columns.dragOverColumn}
          sortField={searchSort.sortField}
          sortDirection={searchSort.sortDirection}
          handleDragStart={columns.handleDragStart}
          handleDragOver={columns.handleDragOver}
          handleDragLeave={columns.handleDragLeave}
          handleDrop={columns.handleDrop}
          handleDragEnd={columns.handleDragEnd}
          handleSort={searchSort.handleSort}
          handleResizeStart={columns.handleResizeStart}
        />
        <NestedTableBody
          searchLoading={searchSort.searchLoading}
          loading={rows.loading}
          columnOrder={columns.columnOrder}
          hiddenColumns={columns.hiddenColumns}
          filteredAndSortedRows={searchSort.filteredAndSortedRows}
          savingRows={rows.savingRows}
          selectedRows={selection.selectedRows}
          handleRowSelect={selection.handleRowSelect}
          inputRefs={inputRefs}
          localRows={rows.localRows}
          setLocalRows={rows.setLocalRows}
          getItemById={rows.getItemById}
          itemMetaById={rows.itemMetaById}
          resetSortingOnEdit={searchSort.resetSortingOnEdit}
          handleTableKeyDown={keyboard.handleTableKeyDown}
          calculatePriceValue={rows.calculatePriceValue}
          handleQuantityClick={quantityModal.handleQuantityClick}
          isLocked={isLocked}
        />
        </table>
      </div>
    </>
  );

  return (
    <>
      <div className="w-full mt-6">
        {renderTableContent()}

        {/* Footer with totals */}
        <DocumentNestedTableFooter rows={rows.localRows} />

        <BulkDeleteModal
          show={selection.showDeleteModal}
          onCancel={() => selection.setShowDeleteModal(false)}
          onConfirm={selection.handleConfirmDelete}
          deletingRows={selection.deletingRows}
          selectedRows={selection.selectedRows}
        />

        {/* Issue serials modal */}
        <IssueSerialsModal
          isOpen={quantityModal.showQuantityModal}
          onClose={() => {
            quantityModal.setShowQuantityModal(false);
            quantityModal.setSelectedRowForQuantity(null);
          }}
          rowData={quantityModal.selectedRowForQuantity}
          partnerId={quantityModal.selectedPartnerId}
          onConfirm={quantityModal.handleSaveSerialChanges}
        />
      </div>

      {/* Column Manager */}
      <ColumnManager
        hiddenColumns={columns.hiddenColumns}
        onColumnToggle={columns.handleColumnToggle}
        onColumnDrop={columns.handleColumnHide}
      />
    </>
  );
};

export default NestedDocumentTable;
